import React, { useState, useEffect, useCallback } from 'react';
import './style.css';

const PRODUCTS = [
    { key: 'company',     label: '회사',         emoji: '🏢', color: '#01BCD6' },
    { key: 'antstalk',    label: 'AntsTalk',     emoji: '💬', color: '#ec4899' },
    { key: 'antsmeeting', label: 'AntsMeeting',  emoji: '📹', color: '#f97316' },
    { key: 'nodemaster',  label: 'NodeMaster',   emoji: '🧠', color: '#a78bfa' },
    { key: 'ganttchart',  label: 'GanttChart',   emoji: '📅', color: '#34d399' },
    { key: 'antsmusic',   label: 'AntsMusic',    emoji: '🎵', color: '#8b5cf6' },
    { key: 'skyscribe',   label: 'SkyScribe',    emoji: '🎵', color: '#60a5fa' },
    { key: 'story',       label: 'Story',        emoji: '📖', color: '#94a3b8' },
    { key: 'skynet',      label: 'Skynet',       emoji: '🤖', color: '#22c55e' },
    { key: 'estimate',    label: 'Estimate',     emoji: '💰', color: '#f43f5e' },
    { key: 'store',       label: '서비스마켓',   emoji: '🛒', color: '#10b981' },
    { key: 'ar',          label: 'AR대탈출',     emoji: '🎯', color: '#a855f7' },
    { key: 'minigrow',    label: '미니그로우',   emoji: '🌱', color: '#84cc16' },
    { key: 'malbok',      label: 'AI Agent (말복)', emoji: '🤖', color: '#0ea5e9' },
    { key: 'meta',        label: '메타월드',     emoji: '🎮', color: '#fbbf24' },
];

const ASSET_TYPES = [
    { key: 'ci',       label: '회사 CI',        emoji: '🎨', aspect: '1 / 1.414', twoSided: false, companyOnly: true  },
    { key: 'card',     label: '명함',           emoji: '🪪', aspect: '90 / 54',   twoSided: true,  companyOnly: true  },
    { key: 'flyer',    label: '전단지',         emoji: '📄', aspect: '210 / 297', twoSided: true,  companyOnly: false },
    { key: 'brochure', label: '팜플렛',         emoji: '📂', aspect: '297 / 210', twoSided: true,  companyOnly: false },
    { key: 'catalog',  label: '카탈로그',       emoji: '📚', aspect: '4 / 3',     twoSided: false, companyOnly: false },
    { key: 'poster',   label: '포스터',         emoji: '🖼️', aspect: '707 / 1000', twoSided: false, companyOnly: false },
    { key: 'logo',     label: '로고',           emoji: '⭐', aspect: '1 / 1',     twoSided: false, companyOnly: false },
    { key: 'etc',      label: '기타',           emoji: '📎', aspect: '4 / 3',     twoSided: false, companyOnly: false },
];

const SIDE_LABEL = { front: '앞면', back: '뒷면' };

function formatSize(bytes) {
    if (!bytes) return '-';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function AdminCatalog() {
    const [items, setItems] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null); // null=제품 목록, 'company'=상세
    const [message, setMessage] = useState(null);

    // 슬롯 업로드 모달
    const [uploadModal, setUploadModal] = useState(null); // { product, asset, replaceId? }
    const [form, setForm] = useState({ label: '', file: null, thumbnail: null, visibility: 'public' });
    const [uploading, setUploading] = useState(false);

    const showMsg = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 3500);
    };

    const fetchItems = useCallback(async () => {
        try {
            const res = await fetch('/api/admin/catalog', { credentials: 'include' });
            const data = await res.json();
            if (data.success) setItems(data.items);
        } catch (e) {}
    }, []);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    // 제품별 자료 카운트 (활성만)
    const countByProduct = {};
    const countByProductType = {};
    for (const it of items) {
        if (!it.is_active) continue;
        countByProduct[it.product_key] = (countByProduct[it.product_key] || 0) + 1;
        const k = `${it.product_key}:${it.asset_type}`;
        countByProductType[k] = (countByProductType[k] || 0) + 1;
    }

    const openUpload = (productKey, assetKey, side) => {
        setUploadModal({ product: productKey, asset: assetKey, side: side || null });
        setForm({ label: '', file: null, thumbnail: null, visibility: 'public' });
    };

    const closeModal = () => {
        setUploadModal(null);
        setForm({ label: '', file: null, thumbnail: null, visibility: 'public' });
    };

    const handleUpload = async () => {
        if (!form.label.trim()) return showMsg('error', '라벨을 입력하세요.');
        if (!form.file && !form.thumbnail) {
            return showMsg('error', '원본 파일이나 대표 이미지 중 최소 하나는 필요합니다.');
        }

        setUploading(true);
        try {
            // 1) 원본 (선택) 업로드
            let fileUrl = null, fileName = null, fileSize = null;
            if (form.file) {
                const fd1 = new FormData();
                fd1.append('files', form.file);
                const res1 = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=brand_catalog', { method: 'POST', body: fd1 });
                const data1 = await res1.json();
                if (!data1.files || !data1.files[0]) throw new Error('원본 업로드 실패');
                fileUrl = data1.files[0].url;
                fileName = form.file.name;
                fileSize = form.file.size;
            }

            // 2) 대표 이미지 (선택) 업로드
            let thumbnailUrl = null;
            if (form.thumbnail) {
                const fd2 = new FormData();
                fd2.append('files', form.thumbnail);
                const res2 = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=brand_catalog_thumb', { method: 'POST', body: fd2 });
                const data2 = await res2.json();
                if (data2.files && data2.files[0]) thumbnailUrl = data2.files[0].url;
            }

            // 3) 원본 미등록 + 썸네일만 있으면 썸네일을 다운로드 대상으로
            if (!fileUrl && thumbnailUrl) {
                fileUrl = thumbnailUrl;
                fileName = form.thumbnail.name;
                fileSize = form.thumbnail.size;
            }
            if (!fileUrl) throw new Error('업로드된 파일이 없습니다.');

            const saveRes = await fetch('/api/admin/catalog', {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    product_key: uploadModal.product,
                    asset_type: uploadModal.asset,
                    side: uploadModal.side,
                    label: form.label.trim(),
                    file_url: fileUrl,
                    file_name: fileName,
                    file_size: fileSize,
                    thumbnail_url: thumbnailUrl,
                    visibility: form.visibility,
                }),
            });
            const saveData = await saveRes.json();
            if (!saveData.success) throw new Error(saveData.message);

            showMsg('success', `"${form.label}" 추가됨.`);
            closeModal();
            fetchItems();
        } catch (err) {
            showMsg('error', err.message);
        } finally {
            setUploading(false);
        }
    };

    const updateItem = async (id, patch) => {
        await fetch(`/api/admin/catalog/${id}`, {
            method: 'PUT', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(patch),
        });
        fetchItems();
    };

    const deleteItem = async (id, label) => {
        if (!window.confirm(`"${label}" 삭제할까요?`)) return;
        await fetch(`/api/admin/catalog/${id}`, { method: 'DELETE', credentials: 'include' });
        fetchItems();
    };

    // === 제품 목록 뷰 ===
    if (!selectedProduct) {
        return (
            <div>
                <div className="admin-page-header">
                    <div>
                        <h1 className="admin-page-title">제품 카탈로그 / CI</h1>
                        <p className="admin-page-desc">제품을 선택해서 그 제품의 인쇄물 자료(CI/전단지/팜플렛/카탈로그/포스터/명함/로고)를 업로드·관리합니다.</p>
                    </div>
                </div>

                {message && <Toast m={message} />}

                <div className="catalog-product-grid">
                    {PRODUCTS.map(p => {
                        const availableTypes = ASSET_TYPES.filter(t => !t.companyOnly || p.key === 'company');
                        const cnt = countByProduct[p.key] || 0;
                        const pct = Math.round((cnt / availableTypes.length) * 100);
                        return (
                            <button
                                key={p.key}
                                type="button"
                                onClick={() => setSelectedProduct(p.key)}
                                className="catalog-product-card"
                                style={{ '--p-color': p.color }}
                            >
                                <div className="cpc-head">
                                    <span className="cpc-emoji">{p.emoji}</span>
                                    <span className="cpc-name">{p.label}</span>
                                </div>
                                <div className="cpc-meta">
                                    <span className="cpc-count">{cnt}<small> / {availableTypes.length}</small></span>
                                    <span className="cpc-pct">{pct}%</span>
                                </div>
                                <div className="cpc-bar"><span style={{ width: pct + '%' }} /></div>
                                <div className="cpc-types">
                                    {availableTypes.map(t => {
                                        const has = !!countByProductType[`${p.key}:${t.key}`];
                                        return (
                                            <span
                                                key={t.key}
                                                className={'cpc-type' + (has ? ' has' : '')}
                                                title={t.label}
                                            >{t.emoji}</span>
                                        );
                                    })}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    // === 제품 상세 뷰 (8 슬롯) ===
    const productInfo = PRODUCTS.find(p => p.key === selectedProduct);
    const productItems = items.filter(it => it.product_key === selectedProduct);

    return (
        <div>
            <div className="admin-page-header">
                <div>
                    <button type="button" onClick={() => setSelectedProduct(null)} className="catalog-back-btn">
                        ← 제품 목록
                    </button>
                    <h1 className="admin-page-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: '1.6rem' }}>{productInfo.emoji}</span>
                        <span>{productInfo.label} 카탈로그</span>
                    </h1>
                    <p className="admin-page-desc">자료를 클릭해서 업로드·교체·숨김 처리할 수 있습니다.</p>
                </div>
            </div>

            {message && <Toast m={message} />}

            <div className="catalog-slot-grid">
                {ASSET_TYPES.filter(t => !t.companyOnly || selectedProduct === 'company').map(t => {
                    const slotItems = productItems.filter(it => it.asset_type === t.key);

                    // 양면 자산: 앞면/뒷면 sub-슬롯
                    if (t.twoSided) {
                        return (
                            <div key={t.key} className="catalog-slot two-sided" style={{ '--p-color': productInfo.color }}>
                                <div className="cs-head">
                                    <span className="cs-title">
                                        <span style={{ fontSize: '1.15rem' }}>{t.emoji}</span>
                                        <span>{t.label}</span>
                                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', fontWeight: 500 }}>양면</span>
                                    </span>
                                </div>
                                <div className="cs-sides">
                                    {['front', 'back'].map(side => {
                                        const sideItems = slotItems.filter(it => (it.side || 'front') === side);
                                        return (
                                            <div key={side} className="cs-side-col">
                                                <div className="cs-side-label">
                                                    <span>{SIDE_LABEL[side]}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => openUpload(selectedProduct, t.key, side)}
                                                        className="cs-add"
                                                    >+ 추가</button>
                                                </div>
                                                {sideItems.length === 0 ? (
                                                    <div
                                                        className="cs-empty"
                                                        style={{ aspectRatio: t.aspect }}
                                                        onClick={() => openUpload(selectedProduct, t.key, side)}
                                                    >
                                                        <span className="emoji">{t.emoji}</span>
                                                        <span className="hint">업로드</span>
                                                    </div>
                                                ) : (
                                                    <div className="cs-items">
                                                        {sideItems.map(it => renderSlotItem(it, t, updateItem, deleteItem))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div key={t.key} className="catalog-slot" style={{ '--p-color': productInfo.color }}>
                            <div className="cs-head">
                                <span className="cs-title">
                                    <span style={{ fontSize: '1.15rem' }}>{t.emoji}</span>
                                    <span>{t.label}</span>
                                </span>
                                <button type="button" onClick={() => openUpload(selectedProduct, t.key)} className="cs-add">
                                    + 추가
                                </button>
                            </div>
                            {slotItems.length === 0 ? (
                                <div
                                    className="cs-empty"
                                    style={{ aspectRatio: t.aspect }}
                                    onClick={() => openUpload(selectedProduct, t.key)}
                                >
                                    <span className="emoji">{t.emoji}</span>
                                    <span className="hint">클릭해서 업로드</span>
                                </div>
                            ) : (
                                <div className="cs-items">
                                    {slotItems.map(it => renderSlotItem(it, t, updateItem, deleteItem))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* 업로드 모달 */}
            {uploadModal && (() => {
                const t = ASSET_TYPES.find(x => x.key === uploadModal.asset);
                return (
                    <div className="catalog-modal-backdrop" onClick={closeModal}>
                        <div className="catalog-modal" onClick={e => e.stopPropagation()}>
                            <div className="cm-head">
                                <h3>
                                    {t.emoji} {productInfo.label} · {t.label}
                                    {uploadModal.side ? ` (${SIDE_LABEL[uploadModal.side]})` : ''} 업로드
                                </h3>
                                <button type="button" onClick={closeModal} className="cm-close">×</button>
                            </div>
                            <div className="cm-body">
                                <div className="cm-row">
                                    <label>라벨 *</label>
                                    <input
                                        type="text"
                                        value={form.label}
                                        onChange={e => setForm({ ...form, label: e.target.value })}
                                        placeholder={`예: ${productInfo.label} ${t.label} v2 (2026)`}
                                        autoFocus
                                    />
                                </div>
                                <div className="cm-row">
                                    <label>원본 파일 (선택, PDF/AI/PSD/이미지)</label>
                                    <input type="file" onChange={e => setForm({ ...form, file: e.target.files[0] })} />
                                    {form.file && <span className="cm-meta">{form.file.name} ({formatSize(form.file.size)})</span>}
                                </div>
                                <div className="cm-row">
                                    <label>대표 이미지 (선택)</label>
                                    <input type="file" accept="image/*" onChange={e => setForm({ ...form, thumbnail: e.target.files[0] })} />
                                    {form.thumbnail && (
                                        <div style={{ marginTop: 8 }}>
                                            <img src={URL.createObjectURL(form.thumbnail)} alt="" style={{ maxWidth: 140, maxHeight: 100, borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)' }} />
                                        </div>
                                    )}
                                    <span className="cm-meta">
                                        💡 원본 파일이 없으면 대표 이미지를 다운로드 파일로 사용합니다. 원본이 이미지면 대표 이미지 비워둬도 자동으로 썸네일로 사용됩니다. (둘 중 하나는 필수)
                                    </span>
                                </div>
                                <div className="cm-row">
                                    <label>공개 여부</label>
                                    <select value={form.visibility} onChange={e => setForm({ ...form, visibility: e.target.value })}>
                                        <option value="public">🌐 공개</option>
                                        <option value="internal">🔒 내부 전용</option>
                                    </select>
                                </div>
                            </div>
                            <div className="cm-foot">
                                <button type="button" onClick={closeModal} className="cm-btn ghost">취소</button>
                                <button type="button" onClick={handleUpload} disabled={uploading} className="cm-btn primary">
                                    {uploading ? '업로드 중...' : '업로드'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

function renderSlotItem(it, t, updateItem, deleteItem) {
    const thumb = it.thumbnail_url || (it.file_url && /\.(png|jpe?g|gif|webp|svg)$/i.test(it.file_url) ? it.file_url : null);
    return (
        <div key={it.id} className={'cs-item' + (it.is_active ? '' : ' inactive')}>
            <div className="cs-thumb" style={{ aspectRatio: t.aspect }}>
                {thumb ? (
                    <img src={thumb} alt={it.label} />
                ) : (
                    <div className="cs-thumb-empty">
                        <span className="emoji">{t.emoji}</span>
                        <span className="text">이미지 준비 중</span>
                    </div>
                )}
            </div>
            <div className="cs-label">{it.label}</div>
            <div className="cs-sub">
                {it.side ? `${SIDE_LABEL[it.side]} · ` : ''}{formatSize(it.file_size)}
            </div>
            <div className="cs-actions">
                <button
                    type="button"
                    onClick={() => updateItem(it.id, { visibility: it.visibility === 'public' ? 'internal' : 'public' })}
                    className={'cs-act vis ' + it.visibility}
                    title={it.visibility === 'public' ? '공개 → 내부로' : '내부 → 공개로'}
                >{it.visibility === 'public' ? '🌐' : '🔒'}</button>
                <a
                    href={it.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cs-act"
                    title="다운로드"
                >⬇</a>
                <button
                    type="button"
                    onClick={() => updateItem(it.id, { is_active: !it.is_active })}
                    className="cs-act"
                    title={it.is_active ? '숨기기' : '복원'}
                >{it.is_active ? '🙈' : '👁'}</button>
                <button
                    type="button"
                    onClick={() => deleteItem(it.id, it.label)}
                    className="cs-act danger"
                    title="삭제"
                >🗑</button>
            </div>
        </div>
    );
}

function Toast({ m }) {
    return (
        <div style={{
            padding: '12px 16px', borderRadius: 10, marginBottom: 16,
            background: m.type === 'success' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
            color: m.type === 'success' ? '#86efac' : '#f87171',
            border: `1px solid ${m.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
            fontSize: 14,
        }}>{m.text}</div>
    );
}
